<template>
  <div class="text-container md:md-text-container">
    <div class="grid grid-cols-1 md:grid-cols-10 gap-5">
      <div v-if="progress === 1" class="md:col-span-8">
        <Heading text="Just before we begin"/>
      </div>
      <div v-if="progress === 1" class="md:col-span-8">
        We’d like to ask for some information about you. Your answers will help us compare
        responses and inform our research.
      </div>
      <div v-if="progress === 1" class="md:col-span-8">
        All your data is anonymous. If you change your mind you can request we delete your data.
      </div>
      <div v-if="currentQuestionSet" class="questions md:col-span-10 gap-10 grid grid-cols-2">
        <div
          v-for="question in currentQuestionSet"
          class="col-span-2 md:col-span-1"
          :key="question.id"
        >
          <div
            v-if="
              question.parentQuestion === null ||
              question.parentQuestion.answer === question.parentAnswer.id
            "
            class="grid grid-cols-6 gap-10 question-block md:md-question-block"
          >
            <div class="question md:md-question col-span-6">
              {{ question.question }}
              <div class="question-explainer" v-if="question.question_type === 'CB'">
                (please select as appropriate)
              </div>
            </div>
            <div class="answers col-span-6">
              <div v-if="question.question_type === 'DD'">
                <select class="drop-down md:md-drop-down" v-model="question.answer">
                  <option
                    v-for="answer in question.answers"
                    v-bind:value="answer.id"
                    :key="answer.id"
                  >
                    {{ answer.answer }}
                  </option>
                </select>
              </div>
              <div v-else-if="question.question_type === 'RB'">
                <div class="grid grid-cols-3 md:grid-cols-4">
                  <div v-for="answer in question.answers" :key="answer.id">
                    <input
                      type="radio"
                      v-model="question.answer"
                      v-bind:value="answer.id"
                      class="radio-button md:md-radio-button"
                    />
                    <div class="radio-label md:md-radio-label">{{ answer.answer }}</div>
                  </div>
                </div>
              </div>
              <div v-else-if="question.question_type === 'CB'">
                <div v-for="answer in question.answers" :key="answer.id" class="md:md-question">
                  <input
                    type="checkbox"
                    :value="answer.id"
                    v-model="question.answer"
                  />
                  {{ answer.answer }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="md:col-span-8">
        <Button v-bind:onClick="continueButton" text="Continue"/>
      </div>
    </div>
    <div v-if="allQuestionSets.length > 1">
      <Progress
        :current=progress
        :total=allQuestionSets.length
      />
    </div>
    <audio ref="aud" src="@/assets/audio/questions.mp3"/>
  </div>
</template>

<script>
import axios from 'axios';
import Button from '@/components/Button.vue';
import Heading from '@/components/Heading.vue';
import Progress from '@/components/Progress.vue';

export default {
  name: 'Questions',
  components: {
    Button,
    Heading,
    Progress,
  },
  data() {
    return {
      allQuestions: [],
      allQuestionSets: [],
      currentQuestionSet: null,
      progress: 1,
    };
  },
  methods: {
    continueButton() {
      if (this.progress < this.allQuestionSets.length) {
        this.progress += 1;
      }
      for (let i = 0; i < this.allQuestionSets.length; i += 1) {
        if (this.currentQuestionSet === this.allQuestionSets[i]) {
          if (this.allQuestionSets[i + 1]) {
            this.currentQuestionSet = this.allQuestionSets[i + 1];
            break;
          } else {
            this.submitQuestions();
          }
        }
      }
    },
    setQuestions() {
      axios
        .get(`${process.env.VUE_APP_BACKEND_URL}/api/demographics/question-groups/`)
        .then((response) => {
          const questionGroups = response.data.results;

          for (let i = 0; i < questionGroups.length; i += 1) {
            const { questions } = questionGroups[i];
            const questionSet = [];

            for (let j = 0; j < questions.length; j += 1) {
              // Check box questions need an array to store multiple answers
              if (questions[j].question_type === 'CB') {
                questions[j].answer = [];
              } else {
                questions[j].answer = null;
              }

              // Setup questions that are followup questions to answers
              questions[j].parentQuestion = null;
              questions[j].parentAnswer = null;
              this.allQuestions.push(questions[j]);
              questionSet.push(questions[j]);
              const { answers } = questions[j];

              for (let k = 0; k < answers.length; k += 1) {
                if (answers[k].follow_up_question) {
                  const followUp = answers[k].follow_up_question;
                  followUp.parentQuestion = questions[j];
                  followUp.parentAnswer = answers[k];
                  this.allQuestions.push(followUp);
                  questionSet.push(followUp);
                }
              }
            }
            this.allQuestionSets.push(questionSet);
          }
          if (this.allQuestionSets) {
            [this.currentQuestionSet] = this.allQuestionSets;
          }
        })
        .catch((error) => {
          this.$store.commit('setErrorMessage', error);
        });
    },
    submitQuestions() {
      const data = [];

      for (let i = 0; i < this.allQuestions.length; i += 1) {
        const question = this.allQuestions[i];

        if (question.parentQuestion !== null) {
          const { parentQuestion, parentAnswer } = question;
          if (parentQuestion.answer !== parentAnswer.id) {
            // eslint-disable-next-line no-continue
            continue;
          }
        }

        const dataEntry = {
          question: this.allQuestions[i].id,
        };

        // Answers need to be in array for the API
        if (!question.answer) {
          dataEntry.answers = [];
        } else if (Array.isArray(question.answer)) {
          dataEntry.answers = question.answer;
        } else {
          dataEntry.answers = [question.answer];
        }

        data.push(dataEntry);
      }

      axios
        .post(
          `${process.env.VUE_APP_BACKEND_URL}/api/sessions/submit-demographics/`,
          {
            uuid: this.$store.state.sessionId,
            questions_and_answers: data,
          },
        )
        .then(() => {
          this.$refs.aud.pause();
          this.$store.commit('setView', 'Breathing');
        })
        .catch((error) => {
          this.$store.commit('setErrorMessage', error);
        });
    },
  },
  mounted() {
    this.$refs.aud.play();
    this.setQuestions();
  },
};
</script>

<style scoped>
.drop-down {
  background-color: #fff;
  border-color: #c2c1c1;
  border-radius: 5rem;
  border-width: 1px;
  box-shadow: 0px 1px #aaaaaa55;
  width: 100%;
}

.drop-down:focus {
  outline: none;
}

.radio-button {
  display: inline;
}

.radio-label {
  display: inline;
  margin: 0 10px;
}

.question {
  text-align: left;
}

.questions {
  margin: 20px 0;
}

.question-block {
  font-size: .81rem;
  margin: 20px 0;
}

.question-explainer {
  font-size: .75em;
  font-style: italic;
}

@responsive {
  .md-drop-down {
    width: 50%;
  }

  .md-radio-label, .md-question, .md-question-block {
    font-size: 0.81em;
  }
}
</style>
